import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, Chip, CircularProgress, Alert,
  Grid, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SchoolIcon from '@mui/icons-material/School';
import { useNavigate, useParams } from 'react-router-dom';
import { getDiscipulado, deletarDiscipulado, atualizarDiscipulado, vincularCurso } from '../../api/discipulado';
import api from '../../axios';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContextNavBar from '../../components/ContextNavBar';
import ModuleHeader from '../../components/ModuleHeader';

const STATUS_COLOR = {
  ativo: 'success', pausado: 'warning', concluido: 'info', cancelado: 'error'
};
const STATUS_LABEL = {
  ativo: 'Ativo', pausado: 'Pausado', concluido: 'Concluído', cancelado: 'Cancelado'
};

function DiscipuladoDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [discipulado, setDiscipulado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ status: '', discipulador_id: '', data_inicio: '', observacoes: '' });
  const [saving, setSaving] = useState(false);
  const [membros, setMembros] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [vinculoOpen, setVinculoOpen] = useState(false);
  const [cursoSelected, setCursoSelected] = useState('');

  const carregar = useCallback(async () => {
    try {
      const [data, membrosRes] = await Promise.all([
        getDiscipulado(id),
        api.get('/secretaria/membros').catch(() => ({ data: [] })),
      ]);
      setDiscipulado(data);
      setMembros(Array.isArray(membrosRes.data) ? membrosRes.data : []);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar discipulado');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleDeletar = async () => {
    try {
      await deletarDiscipulado(id);
      navigate('/discipulados');
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao deletar discipulado');
    }
  };

  const abrirEditar = () => {
    setEditForm({
      discipulador_id: discipulado.discipulador_id || '',
      status: discipulado.status || 'ativo',
      observacoes: discipulado.observacoes || '',
      data_inicio: discipulado.data_inicio ? discipulado.data_inicio.slice(0, 10) : new Date().toISOString().split('T')[0],
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        discipulador_id: editForm.discipulador_id ? parseInt(editForm.discipulador_id, 10) : undefined,
      };
      await atualizarDiscipulado(id, payload);
      setEditOpen(false);
      carregar();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao atualizar discipulado');
    }
    setSaving(false);
  };

  const abrirVinculo = async () => {
    try {
      const res = await api.get('/cursos/');
      const lista = Array.isArray(res.data) ? res.data : [];
      setCursos(lista);
      setCursoSelected('');
      if (lista.length === 0) {
        alert('Nenhum curso disponível. Crie um curso primeiro.');
        return;
      }
      setVinculoOpen(true);
    } catch {
      alert('Erro ao carregar cursos');
    }
  };

  const handleVincular = async () => {
    if (!cursoSelected) return;
    try {
      await vincularCurso(id, parseInt(cursoSelected, 10));
      setVinculoOpen(false);
      carregar();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao vincular curso');
    }
  };

  const formatarData = (data) => {
    if (!data) return '—';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <>
        <Header />
        <ContextNavBar context="discipulado" />
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        </Container>
        <Footer />
      </>
    );
  }

  if (erro) {
    return (
      <>
        <Header />
        <ContextNavBar context="discipulado" />
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
          <Alert severity="error">{erro}</Alert>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <ContextNavBar context="discipulado" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/discipulados')} size="small">Voltar</Button>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 0 } }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Discipulado</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip label={STATUS_LABEL[discipulado.status] || discipulado.status} color={STATUS_COLOR[discipulado.status] || 'default'} size="small" />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<SchoolIcon />} onClick={abrirVinculo} size="small">Vincular Curso</Button>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={abrirEditar}>Editar</Button>
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDeletar}>Deletar</Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Informações</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Discipulador</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{discipulado.discipulador_nome || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Discipulando</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{discipulado.discipulando_nome || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Data Início</Typography>
                    <Typography variant="body2">{formatarData(discipulado.data_inicio)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Data Fim</Typography>
                    <Typography variant="body2">{formatarData(discipulado.data_fim)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Observações</Typography>
                    <Typography variant="body2">{discipulado.observacoes || 'Nenhuma observação.'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Cursos Vinculados</Typography>
                </Box>
                {(discipulado.cursos || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Nenhum curso vinculado.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(discipulado.cursos || []).map(curso => (
                      <Chip key={curso.id} label={curso.titulo || curso.nome || `Curso #${curso.id}`} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Editar Discipulado</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Discipulador</InputLabel>
              <Select value={editForm.discipulador_id} label="Discipulador" onChange={e => setEditForm(f => ({ ...f, discipulador_id: e.target.value }))}>
                <MenuItem value="">Selecione</MenuItem>
                {membros.map(m => (
                  <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={editForm.status} label="Status" onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                {Object.entries(STATUS_LABEL).map(([k, lbl]) => (
                  <MenuItem key={k} value={k}>{lbl}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Data de Início" type="date" value={editForm.data_inicio} onChange={e => setEditForm(f => ({ ...f, data_inicio: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Observações" value={editForm.observacoes} onChange={e => setEditForm(f => ({ ...f, observacoes: e.target.value }))} fullWidth multiline rows={3} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={handleEditSave} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={18} /> : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={vinculoOpen} onClose={() => setVinculoOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Vincular Curso</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Curso</InputLabel>
              <Select value={cursoSelected} label="Curso" onChange={e => setCursoSelected(e.target.value)}>
                <MenuItem value="">Selecione</MenuItem>
                {cursos.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.titulo}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVinculoOpen(false)}>Cancelar</Button>
            <Button onClick={handleVincular} variant="contained">Vincular</Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </>
  );
}

export default DiscipuladoDetalhe;
